<template>
  <v-card :class="inline ? 'pa-0' : 'pa-6 pb-4'" :flat="inline" test-id="intervener-signature-qr-panel">
    <!-- Título y nombre del firmante solo en el diálogo: dentro de la tarjeta del interviniente
         son redundantes, ya que la propia tarjeta identifica al firmante -->
    <v-card-title v-if="!inline" class="pa-0 pb-2 d-flex justify-center" test-id="qr-panel-title">
      <span class="cts-font-size-title font-weight-medium text-center">
        {{ $vuetify.lang.t("$vuetify.faceToFaceQrTitle") }}
      </span>
    </v-card-title>
    <v-card-text class="pa-0">
      <v-row v-if="!inline" class="mb-2" dense justify="center" test-id="qr-panel-signer-name">
        <span class="cts-font-size-2 font-weight-medium">{{ name }}</span>
      </v-row>

      <!-- Inline: instrucciones a la izquierda y QR a la derecha, con el botón de acceso debajo;
           en el diálogo las tres piezas se apilan centradas -->
      <v-row :align="inline ? 'center' : 'start'" dense justify="center">
        <v-col
          :class="inline ? 'text-center text-md-left py-4 px-md-6' : 'text-center'"
          :md="inline ? 7 : 12"
          cols="12"
          test-id="qr-panel-instructions"
        >
          <!-- Inline el diálogo no aporta su título, así que el bloque lleva el suyo -->
          <p v-if="inline" class="cts-font-size-title font-weight-medium mb-2"
             test-id="qr-panel-inline-title">
            {{ $vuetify.lang.t("$vuetify.signatureCeremonyAccess") }}
          </p>

          <p class="cts-font-size-2 mb-0">
            {{ $vuetify.lang.t("$vuetify.faceToFaceQrInstructions") }}
          </p>

          <!-- Inline el acceso va justo debajo del texto, en la misma columna -->
          <!-- inline-flex en columna: el enlace de copia queda centrado respecto al botón de acceso
               aunque el bloque esté alineado a la izquierda -->
          <div v-if="inline" class="mt-4 d-inline-flex flex-column align-center">
            <!-- El href habilita el menú contextual del navegador (copiar enlace); el clic se intercepta -->
            <v-btn
              :href="signatureUrl"
              color="var(--primary-alternative)"
              dark
              test-id="open-on-this-device-qr-panel"
              @click.prevent="$emit('openFrontRenderer')"
            >
              {{ $vuetify.lang.t("$vuetify.openOnThisDevice") }}
            </v-btn>

            <!-- En los dos modos el enlace de copia va bajo el botón de acceso -->
            <div v-if="copyAccessLink" class="mt-2">
              <CopyAccessLinkButton :url="signatureUrl" test-id="copy-access-link-qr-panel" />
            </div>
          </div>
        </v-col>

        <v-col
          :class="inline ? 'justify-center justify-md-end py-4 px-md-6' : 'justify-center my-4'"
          :md="inline ? 5 : 12"
          class="d-flex align-center"
          cols="12"
        >
          <!-- Compensa la anchura del botón de zoom para que el QR quede centrado y no
               desplazado media anchura de botón hacia la izquierda -->
          <div class="qr-zoom-balance"></div>

          <!-- level L: menos módulos para el mismo enlace, para que el QR se lea bien aunque se
               muestre a un tamaño reducido dentro de la tarjeta -->
          <qrcode-vue :value="signatureUrl" :size="size" level="L" />

          <v-btn
            :aria-label="$vuetify.lang.t('$vuetify.enlargeQr')"
            :title="$vuetify.lang.t('$vuetify.enlargeQr')"
            class="ml-2"
            color="var(--primary)"
            icon
            test-id="zoom-qr-panel"
            @click="zoomDialog = true"
          >
            <v-icon>zoom_in</v-icon>
          </v-btn>
        </v-col>

        <!-- En el diálogo el acceso cierra la columna apilada -->
        <v-col v-if="!inline" class="text-center" cols="12">
          <v-btn
            :href="signatureUrl"
            color="var(--primary-alternative)"
            dark
            test-id="open-on-this-device-qr-panel"
            @click.prevent="$emit('openFrontRenderer')"
          >
            {{ $vuetify.lang.t("$vuetify.openOnThisDevice") }}
          </v-btn>

          <div v-if="copyAccessLink" class="mt-2">
            <CopyAccessLinkButton :url="signatureUrl" test-id="copy-access-link-qr-panel" />
          </div>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions v-if="closable">
      <v-row dense justify="center">
        <v-col cols="12" class="d-flex justify-center pa-0 mt-4">
          <v-btn color="var(--primary)" outlined test-id="close-qr-panel" @click="$emit('close')">
            {{ $vuetify.lang.t("$vuetify.close") }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>

    <v-dialog v-if="zoomDialog" v-model="zoomDialog" width="420">
      <v-card class="pa-6 pb-4" test-id="zoom-qr-dialog">
        <v-card-title class="pa-0 pb-4 d-flex justify-center">
          <span class="cts-font-size-title font-weight-medium text-center">
            {{ $vuetify.lang.t("$vuetify.faceToFaceQrTitle") }}
          </span>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-row dense justify="center">
            <qrcode-vue :value="signatureUrl" :size="zoomSize" level="L" />
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-row dense justify="center">
            <v-btn color="var(--primary)" outlined test-id="close-zoom-qr-panel" @click="zoomDialog = false">
              {{ $vuetify.lang.t("$vuetify.close") }}
            </v-btn>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import QrcodeVue from "qrcode.vue";
import CopyAccessLinkButton from "@/components/sections/transactions/components/CopyAccessLinkButton.vue";

export default {
  name: "IntervenerSignatureQrPanel",
  components: { CopyAccessLinkButton, QrcodeVue },
  props: {
    name: String,
    signatureUrl: String,
    // Muestra el botón de cerrar: solo aplica cuando el panel se usa dentro de un diálogo
    closable: Boolean,
    // Renderizado dentro de la tarjeta del interviniente: distribución horizontal, sin elevación,
    // sin padding de tarjeta y sin título ni nombre del firmante, que ahí serían redundantes
    inline: Boolean,
    // Muestra el enlace "Copiar URL al portapapeles" (faceToFaceCopyAccessLink del behaviour layer)
    copyAccessLink: Boolean,
    size: {
      type: Number,
      default: 240
    },
    zoomSize: {
      type: Number,
      default: 320
    }
  },
  data() {
    return {
      zoomDialog: false
    };
  },
};
</script>

<style lang="scss">
// Misma huella que el botón de zoom (v-btn icon de 36px más su ml-2 de 8px), para que el
// código quede centrado y no desplazado por tener el botón solo a un lado
.qr-zoom-balance {
  width: 44px;
  flex: 0 0 auto;
}
</style>
